import type { Op, MachineProfile, ResolveParams, Metrics } from '../types/domain';

/**
 * Export builders ported from the pre-Studio-2.0 gallery, which offered five formats. The Vite
 * migration kept only raw G-code, so a design could no longer leave the studio as anything a
 * FullControl workflow or a printer with a start macro would accept.
 */
export type ExportFormat = 'gcode' | 'gcode-macros' | 'dry-json' | 'metrics-json' | 'fullcontrol-py';

export const EXPORT_FORMATS: Array<{ id: ExportFormat; label: string }> = [
  { id: 'gcode', label: 'G-code' },
  { id: 'gcode-macros', label: 'G-code + macros' },
  { id: 'dry-json', label: 'Dry L1 JSON' },
  { id: 'metrics-json', label: 'Metrics JSON' },
  { id: 'fullcontrol-py', label: 'FullControl Python' },
];

export interface MacroOptions {
  header: boolean;
  heat: boolean;
  home: boolean;
  primeLine: boolean;
  primeBlob: boolean;
  present: boolean;
  cooldown: boolean;
  motorsOff: boolean;
  relativeE: boolean;
  nozzleTemp: number;
  bedTemp: number;
}

export const DEFAULT_MACRO_OPTIONS: MacroOptions = {
  header: true,
  heat: true,
  home: true,
  primeLine: true,
  primeBlob: false,
  present: true,
  cooldown: true,
  motorsOff: true,
  relativeE: false,
  nozzleTemp: 210,
  bedTemp: 60,
};

/** Fixed-decimal G-code number, matching the emitter's own formatting for these words. */
function gnum(value: number, decimals: number): string {
  if (!Number.isFinite(value)) return '0';
  return value.toFixed(decimals);
}

/** Python literal for a number, or `None`. Trimmed to 12 significant digits like the original. */
function pyNumber(value: number | null | undefined): string {
  if (value == null) return 'None';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'None';
  return String(Number.parseFloat(n.toPrecision(12)));
}

function fcPointExpr(fields: { x?: number | null; y?: number | null; z?: number | null }): string {
  const entries: string[] = [];
  for (const key of ['x', 'y', 'z'] as const) {
    const value = fields[key];
    if (value != null) entries.push(`${key}=${pyNumber(value)}`);
  }
  return `fc.Point(${entries.join(', ')})`;
}

/**
 * Translate L1 ops into a FullControl `steps` list. Ops with no FullControl equivalent are kept as
 * comments rather than dropped — a silently shorter program would be the wrong part, and the
 * comment is what tells the reader which step is missing.
 */
export function fullControlPython(ops: Op[], designLabel: string, machineName: string): string {
  const lines: string[] = [
    'import fullcontrol as fc',
    '',
    '# Generated by Dry from L1 ops.',
    '# Requires FullControl. Dry-only operations are preserved below as comments.',
    `# Design: ${designLabel || 'generated design'}`,
    `# Machine: ${machineName}`,
    '',
    'steps = []',
  ];

  ops.forEach((op, index) => {
    const raw = op as unknown as Record<string, unknown>;
    if (!raw || typeof raw !== 'object') {
      lines.push(`# Unsupported literal op ${index}: ${JSON.stringify(op)}`);
      return;
    }
    const kind = raw.op;
    if (kind === 'geometry') {
      lines.push(
        `steps.append(fc.ExtrusionGeometry(width=${pyNumber(raw.width as number)}, height=${pyNumber(raw.height as number)}))`,
      );
    } else if (kind === 'extruder') {
      lines.push(`steps.append(fc.Extruder(on=${raw.on ? 'True' : 'False'}))`);
    } else if (kind === 'speed') {
      if (raw.print != null) lines.push(`steps.append(fc.Printer(print_speed=${pyNumber(raw.print as number)}))`);
      else lines.push(`# Dry speed op ${index}: ${JSON.stringify(op)}`);
    } else if (kind === 'move') {
      lines.push(`steps.append(${fcPointExpr(raw as never)})`);
    } else if (kind === 'arc') {
      const direction = raw.clockwise ? 'clockwise' : 'anticlockwise';
      lines.push(
        `steps.append(fc.Arc(centre=${fcPointExpr({ x: raw.cx as number, y: raw.cy as number })}, end=${fcPointExpr(raw as never)}, direction='${direction}'))`,
      );
    } else {
      lines.push(`# Dry-only op ${index}: ${JSON.stringify(op)}`);
    }
  });

  lines.push('', '# Pass `steps` into your FullControl workflow, for example transform/preview/export.');
  return `${lines.join('\n')}\n`;
}

export function macroPrefixLines(
  options: MacroOptions,
  machine: MachineProfile,
  params: ResolveParams,
  designLabel: string,
): string[] {
  const lines: string[] = [];
  const { nozzleTemp, bedTemp, relativeE } = options;

  if (options.header) {
    lines.push(
      `; Dry export: ${designLabel || 'generated design'}`,
      `; Machine: ${machine.name} (${machine.manufacturer})`,
      'G21 ; millimeters',
      'G90 ; absolute XYZ',
      relativeE ? 'M83 ; relative extrusion' : 'M82 ; absolute extrusion',
    );
  }
  if (options.heat) {
    if (bedTemp > 0) lines.push(`M140 S${gnum(bedTemp, 0)} ; set bed temperature`);
    if (nozzleTemp > 0) lines.push(`M104 S${gnum(nozzleTemp, 0)} ; set nozzle temperature`);
    if (bedTemp > 0) lines.push(`M190 S${gnum(bedTemp, 0)} ; wait for bed`);
    if (nozzleTemp > 0) lines.push(`M109 S${gnum(nozzleTemp, 0)} ; wait for nozzle`);
  }
  if (options.home) lines.push('G28 ; home axes');
  if (options.primeLine) {
    lines.push(
      '; prime line',
      'M83 ; temporary relative extrusion for primer',
      'G92 E0',
      `G0 X5 Y5 Z0.28 F${gnum(params.travel_speed, 0)}`,
      'G1 X65 Y5 E6 F600',
      'G1 X110 Y5 E4 F600',
      'G92 E0',
    );
    if (!relativeE) lines.push('M82 ; restore absolute extrusion');
  }
  if (options.primeBlob) {
    lines.push(
      '; prime blob',
      'M83 ; temporary relative extrusion for primer',
      'G92 E0',
      'G1 E5 F300',
      'G4 S1',
      'G1 E-0.8 F1800',
      'G92 E0',
    );
    if (!relativeE) lines.push('M82 ; restore absolute extrusion');
  }
  return lines;
}

export function macroSuffixLines(
  options: MacroOptions,
  machine: MachineProfile,
  params: ResolveParams,
): string[] {
  const lines: string[] = [];
  if (options.present) {
    // The old gallery read a `park` field off its printer profiles. The machine catalog has no such
    // field, so park at the front-left of the declared envelope instead of inventing coordinates
    // that might sit outside it.
    const parkX = machine.build_volume.x[0];
    const parkY = machine.build_volume.y[1];
    lines.push(
      '; present print',
      'G91',
      'G0 Z10.00 F1200',
      'G90',
      `G0 X${gnum(parkX, 2)} Y${gnum(parkY, 2)} F${gnum(params.travel_speed, 0)}`,
    );
  }
  if (options.cooldown) lines.push('M104 S0', 'M140 S0', 'M106 S0');
  if (options.motorsOff) lines.push('M84');
  return lines;
}

export interface ExportContext {
  format: ExportFormat;
  gcodeLines: string[];
  ops: Op[];
  metrics: Metrics | null;
  macros: MacroOptions;
  machine: MachineProfile;
  params: ResolveParams;
  designLabel: string;
}

export function buildExportText(ctx: ExportContext): string {
  switch (ctx.format) {
    case 'gcode':
      return `${ctx.gcodeLines.join('\n')}\n`;
    case 'gcode-macros':
      return `${[
        ...macroPrefixLines(ctx.macros, ctx.machine, ctx.params, ctx.designLabel),
        ...ctx.gcodeLines,
        ...macroSuffixLines(ctx.macros, ctx.machine, ctx.params),
      ].join('\n')}\n`;
    case 'dry-json':
      return `${JSON.stringify({ design: ctx.designLabel, params: ctx.params, ops: ctx.ops }, null, 2)}\n`;
    case 'metrics-json':
      return `${JSON.stringify(ctx.metrics ?? {}, null, 2)}\n`;
    case 'fullcontrol-py':
      return fullControlPython(ctx.ops, ctx.designLabel, `${ctx.machine.name} (${ctx.machine.manufacturer})`);
    default:
      return '';
  }
}

export function exportExtension(format: ExportFormat): string {
  if (format === 'dry-json' || format === 'metrics-json') return 'json';
  if (format === 'fullcontrol-py') return 'py';
  return 'gcode';
}

export function exportFilename(format: ExportFormat, designLabel: string): string {
  const safe =
    designLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'dry-export';
  return `${safe}.${exportExtension(format)}`;
}
